/**
 * How many persons — the second step of a tiffin order, and the only thing
 * on it.
 *
 * Counted in ones, from one, up to the kitchen's limit. Each person gets the
 * box just built at every meal of the plan, and the total below says so.
 */

import { StyleSheet } from 'react-native';

import { validatePersons } from '@mfa/domain';
import { space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { ArrowIcon } from '../../components/common/Icon';
import { CheckoutHeader } from '../../components/donation/CheckoutHeader';
import { TIFFIN_STEPS } from '../../components/donation/CheckoutStepper';
import { CountStepper } from '../../components/tiffin/CountStepper';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Text } from '../../components/common/Text';
import { TiffinTotalFooter, useTiffinCheckout } from './useTiffinCheckout';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

export function TiffinPersonsScreen({ navigation }: DonorScreenProps<'TiffinPersons'>) {
  const t = useT();
  const checkout = useTiffinCheckout(navigation);
  const { tiffin, draft } = checkout;
  const error = tiffin ? validatePersons(draft.persons, tiffin) : null;

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-tiffin-persons"
      subHeader={
        <CheckoutHeader
          current={1}
          steps={TIFFIN_STEPS}
          title={t('How Many Persons')}
          subtitle={t('Each person gets the box you built, at every meal')}
        />
      }
      pinnedFooter={
        <TiffinTotalFooter checkout={checkout}>
          <Button
            label={t('Continue')}
            onPress={() => navigation.navigate('TiffinContact')}
            disabled={Boolean(error) || !tiffin}
            trailingIcon={<ArrowIcon />}
            testID="cta-tiffin-persons-continue"
          />
        </TiffinTotalFooter>
      }
    >
      <Card title={t('How many persons?')} testID="tiffin-persons">
        <CountStepper
          value={draft.persons}
          max={tiffin?.maxPersons ?? 1}
          onChange={draft.setPersons}
          unit={['Person', 'Persons']}
          actions={['Fewer persons', 'More persons']}
          testID="persons-stepper"
        />
        <Text variant="caption" tone="tertiary" align="center" style={styles.caption}>
          {t('Each person gets the box you built.')}
        </Text>
        {error ? (
          <Text variant="caption" tone="danger" align="center">{t(error)}</Text>
        ) : null}
      </Card>
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  caption: {
    marginTop: space.sm,
  },
});
